import React from 'react';
import { UserSettings } from '../../types/index';

interface BusinessDetailsCardProps {
  preferences: UserSettings;
  onUpdate?: (preferences: UserSettings) => void;
}

const FIELD_LIMITS = {
  agentGoal: 1500,
  agentInstructions: 8000,
  agentTone: 2000,
  agentRestrictions: 4000,
  businessInformation: 30000,
};

const BusinessDetailsCard: React.FC<BusinessDetailsCardProps> = ({ preferences, onUpdate }) => {
  const handleChange = (field: keyof typeof FIELD_LIMITS, value: string) => {
    if (onUpdate) {
      onUpdate({
        ...preferences,
        [field]: value,
      });
    }
  };

  const renderTextarea = (
    field: keyof typeof FIELD_LIMITS,
    label: string,
    description: string,
    placeholder: string,
    heightClass: string = 'h-32',
  ) => {
    const value = preferences[field] || '';
    const limit = FIELD_LIMITS[field];

    return (
      <div>
        <label className="block text-primary text-sm font-bold mb-2">
          {label}
          <span className="text-gray-500 font-normal ml-2">
            ({value.length}/{limit} caracteres)
          </span>
        </label>
        <p className="text-gray-600 text-sm mb-2">{description}</p>
        <textarea
          value={value}
          onChange={(e) => handleChange(field, e.target.value)}
          placeholder={placeholder}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${heightClass}`}
          maxLength={limit}
        />
      </div>
    );
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
      <h2 className="text-xl font-semibold text-primary mb-4">
        Instrucciones del Agente y Base de Conocimiento
      </h2>
      <p className="text-gray-600 mb-4">
        Define cómo debe comportarse el agente y qué información puede usar para responder sobre
        tu negocio. Las reglas de seguridad de la plataforma siempre tienen prioridad.
      </p>
      <div className="space-y-5">
        {renderTextarea(
          'agentGoal',
          'Objetivo del agente',
          'Qué debe lograr el asistente durante la conversación.',
          'Ej. Calificar prospectos, responder dudas sobre servicios, orientar a clientes actuales y ayudar a agendar citas cuando aplique.',
        )}
        {renderTextarea(
          'agentInstructions',
          'Instrucciones para el agente',
          'Qué información debe recopilar y cómo debe guiar la conversación.',
          'Ej. Recopila nombre, teléfono, tipo de servicio, ubicación y fecha deseada antes de recomendar una solución. Si falta información, haz una pregunta breve a la vez.',
          'h-44',
        )}
        {renderTextarea(
          'agentTone',
          'Tono y estilo',
          'Cómo debe sonar el asistente al responder.',
          'Ej. Responde de forma amable, profesional y breve. Usa un tono cercano, evita tecnicismos innecesarios y no uses emojis.',
        )}
        {renderTextarea(
          'agentRestrictions',
          'Restricciones',
          'Qué no debe prometer, responder o hacer el asistente.',
          'Ej. No prometas descuentos no autorizados, no confirmes disponibilidad sin revisar el calendario y no respondas preguntas que no estén relacionadas con el negocio.',
          'h-40',
        )}
        {renderTextarea(
          'businessInformation',
          'Base de conocimiento del negocio',
          'Información factual que el agente puede consultar para responder preguntas del negocio.',
          'Ej. Incluye servicios, precios, zonas de cobertura, horarios, ubicación, preguntas frecuentes, políticas de cancelación, garantías y cualquier información que el agente pueda usar para responder.',
          'h-64',
        )}
      </div>
    </div>
  );
};

export default BusinessDetailsCard;
